// ===========================================
// Payment Plans API Functions
// ===========================================
// API calls for payment plan management endpoints

import { apiClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export type PaymentPlanType = 'TERM' | 'MONTHLY' | 'FORTNIGHTLY' | 'WEEKLY' | 'CUSTOM';

export type PaymentInterval = 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY';

export interface PaymentPlan {
  id: string;
  schoolId: string;
  name: string;
  type: PaymentPlanType;
  description: string | null;
  installments: number | null;
  interval: PaymentInterval | null;
  discountPercent: string | null;
  surchargePercent: string | null;
  stripeProductId: string | null;
  stripePriceId: string | null;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    subscriptions: number;
  };
}

export interface PaymentPlanFilters {
  isActive?: boolean;
  type?: PaymentPlanType;
}

export interface CreatePaymentPlanInput {
  name: string;
  type: PaymentPlanType;
  description?: string;
  installments?: number;
  interval?: PaymentInterval;
  discountPercent?: number;
  surchargePercent?: number;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface UpdatePaymentPlanInput {
  name?: string;
  type?: PaymentPlanType;
  description?: string | null;
  installments?: number | null;
  interval?: PaymentInterval | null;
  discountPercent?: number | null;
  surchargePercent?: number | null;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface PaymentPlanCalculation {
  paymentPlan: {
    id: string;
    name: string;
    type: PaymentPlanType;
  };
  baseAmount: number;
  adjustedAmount: number;
  discountAmount: number;
  surchargeAmount: number;
  installmentAmount: number | null;
  totalInstallments: number | null;
}

// ===========================================
// API RESPONSE TYPE
// ===========================================

interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
}

interface PaginatedResponse<T> {
  paymentPlans: T[];
  total: number;
}

// ===========================================
// ADMIN - PAYMENT PLANS API
// ===========================================

export const adminPaymentPlansApi = {
  /**
   * Get all payment plans for the school
   */
  getAll: async (filters?: PaymentPlanFilters): Promise<PaginatedResponse<PaymentPlan>> => {
    const response = await apiClient.get<ApiResponse<PaginatedResponse<PaymentPlan>>>(
      '/admin/payment-plans',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get a single payment plan by ID
   */
  getById: async (id: string): Promise<PaymentPlan> => {
    const response = await apiClient.get<ApiResponse<PaymentPlan>>(
      `/admin/payment-plans/${id}`
    );
    return response.data;
  },

  /**
   * Get the default payment plan
   */
  getDefault: async (): Promise<PaymentPlan | null> => {
    const response = await apiClient.get<ApiResponse<PaymentPlan | null>>(
      '/admin/payment-plans/default'
    );
    return response.data;
  },

  /**
   * Create a new payment plan
   */
  create: async (data: CreatePaymentPlanInput): Promise<PaymentPlan> => {
    const response = await apiClient.post<ApiResponse<PaymentPlan>>(
      '/admin/payment-plans',
      data
    );
    return response.data;
  },

  /**
   * Update an existing payment plan
   */
  update: async (id: string, data: UpdatePaymentPlanInput): Promise<PaymentPlan> => {
    const response = await apiClient.patch<ApiResponse<PaymentPlan>>(
      `/admin/payment-plans/${id}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a payment plan
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/payment-plans/${id}`);
  },

  /**
   * Set a payment plan as the default
   */
  setDefault: async (id: string): Promise<PaymentPlan> => {
    const response = await apiClient.put<ApiResponse<PaymentPlan>>(
      `/admin/payment-plans/${id}/default`
    );
    return response.data;
  },
};

// ===========================================
// PARENT - PAYMENT PLANS API
// ===========================================

export const parentPaymentPlansApi = {
  /**
   * Get active payment plans available to parents
   */
  getAll: async (): Promise<PaginatedResponse<PaymentPlan>> => {
    const response = await apiClient.get<ApiResponse<PaginatedResponse<PaymentPlan>>>(
      '/parent/payment-plans'
    );
    return response.data;
  },

  /**
   * Get a specific active payment plan
   */
  getById: async (id: string): Promise<PaymentPlan> => {
    const response = await apiClient.get<ApiResponse<PaymentPlan>>(
      `/parent/payment-plans/${id}`
    );
    return response.data;
  },

  /**
   * Calculate the amount for a payment plan given a base amount
   */
  calculate: async (id: string, baseAmount: number): Promise<PaymentPlanCalculation> => {
    const response = await apiClient.post<ApiResponse<PaymentPlanCalculation>>(
      `/parent/payment-plans/${id}/calculate`,
      { baseAmount }
    );
    return response.data;
  },
};
